// components/patient/Profile.jsx
import React, { useState } from 'react';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Icon } from '../ui/Icon';
import { useBrand } from '../../contexts/BrandContext';
import { useToast } from '../../contexts/ToastContext';
import { DB } from '../../utils/db';
import { authService } from '../../services/authService';
import { US_STATES } from '../../utils/constants';

export function Profile({ user, onNavigate, onUpdate }) {
  const { brand } = useBrand();
  const toast = useToast();
  const [editing, setEditing] = useState(false);
  const [formData, setFormData] = useState({
    firstName: user.firstName || '',
    lastName: user.lastName || '',
    email: user.email || '',
    phone: user.phone || '',
    street: user.address?.street || '',
    apt: user.address?.apt || '',
    city: user.address?.city || '',
    state: user.address?.state || '',
    zip: user.address?.zip || '',
    insuranceProvider: user.insurance?.provider || '',
    memberId: user.insurance?.memberId || '',
    groupNumber: user.insurance?.groupNumber || ''
  });
  
  const handleSave = async () => {
    try {
      const updatedProfile = await authService.updateProfile({
        email: formData.email,
        userAttributes: {
          firstname: formData.firstName,
          lastname: formData.lastName,
          phone: formData.phone
        }
      });

      if (formData.street) {
        try {
          await authService.saveAddress({
            street: formData.street,
            apt: formData.apt,
            city: formData.city,
            state: formData.state,
            zip: formData.zip
          });
          updatedProfile.address = {
            street: formData.street,
            apt: formData.apt,
            city: formData.city,
            state: formData.state,
            zip: formData.zip
          };
        } catch (addrErr) {
          console.warn('Address update failed:', addrErr.message);
        }
      }

      toast.addToast('Profile updated successfully');
      setEditing(false);
      onUpdate && onUpdate(updatedProfile);
    } catch (err) {
      console.warn('API profile update failed, falling back to localStorage:', err.message);
      const updatedUser = {
        ...user,
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        phone: formData.phone,
        address: {
          street: formData.street,
          apt: formData.apt,
          city: formData.city,
          state: formData.state,
          zip: formData.zip
        },
        insurance: formData.insuranceProvider ? {
          provider: formData.insuranceProvider,
          memberId: formData.memberId,
          groupNumber: formData.groupNumber
        } : null
      };
      await DB.set(`user:${user.id}`, updatedUser);
      toast.addToast('Profile updated successfully');
      setEditing(false);
      onUpdate && onUpdate(updatedUser);
    }
  };
  
  return (
    <div style={{
      background: '#F9FAFB',
      minHeight: '100vh',
      padding: '40px 24px',
      fontFamily: 'Libre Franklin, sans-serif'
    }}>
      <div style={{ maxWidth: 800, margin: '0 auto' }}>
        <Button
          variant="ghost"
          icon="arrowLeft"
          onClick={() => onNavigate('dashboard')}
          style={{ marginBottom: 24 }}
        >
          Back to Dashboard
        </Button>
        
        <h1 style={{ fontSize: 32, fontWeight: 700, marginBottom: 8, color: brand.c.navy }}>
          My Profile
        </h1>
        <p style={{ color: '#6B7280', marginBottom: 32 }}>
          Manage your account information and preferences
        </p>
        
        {/* Personal Information */}
        <Card style={{ marginBottom: 24 }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: 24
          }}>
            <h2 style={{ fontSize: 20, fontWeight: 700, color: brand.c.navy }}>
              Personal Information
            </h2>
            {!editing && (
              <Button variant="secondary" size="sm" icon="settings" onClick={() => setEditing(true)}>
                Edit
              </Button>
            )}
          </div>
          
          {editing ? (
            <>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 16 }}>
                <Input
                  label="First Name"
                  value={formData.firstName}
                  onChange={e => setFormData({ ...formData, firstName: e.target.value })}
                />
                <Input
                  label="Last Name"
                  value={formData.lastName}
                  onChange={e => setFormData({ ...formData, lastName: e.target.value })}
                />
              </div>
              <Input
                label="Email"
                type="email"
                value={formData.email}
                onChange={e => setFormData({ ...formData, email: e.target.value })}
              />
              <Input
                label="Phone"
                type="tel"
                value={formData.phone}
                onChange={e => setFormData({ ...formData, phone: e.target.value })}
              />
              
              <div style={{ display: 'flex', gap: 12, marginTop: 24 }}>
                <Button onClick={handleSave}>Save Changes</Button>
                <Button variant="secondary" onClick={() => setEditing(false)}>Cancel</Button>
              </div>
            </>
          ) : (
            <div style={{ display: 'grid', gap: 20 }}>
              <div>
                <div style={{ fontSize: 14, color: '#6B7280', marginBottom: 4 }}>Name</div>
                <div style={{ fontSize: 16, fontWeight: 600 }}>{user.firstName} {user.lastName}</div>
              </div>
              <div>
                <div style={{ fontSize: 14, color: '#6B7280', marginBottom: 4 }}>Email</div>
                <div style={{ fontSize: 16, fontWeight: 600 }}>{user.email}</div>
              </div>
              <div>
                <div style={{ fontSize: 14, color: '#6B7280', marginBottom: 4 }}>Phone</div>
                <div style={{ fontSize: 16, fontWeight: 600 }}>{user.phone || 'Not provided'}</div>
              </div>
              <div>
                <div style={{ fontSize: 14, color: '#6B7280', marginBottom: 4 }}>Date of Birth</div>
                <div style={{ fontSize: 16, fontWeight: 600 }}>{user.dob ? new Date(user.dob).toLocaleDateString() : 'Not provided'}</div>
              </div>
            </div>
          )}
        </Card>
        
        {/* Address */}
        <Card style={{ marginBottom: 24 }}>
          <h2 style={{ fontSize: 20, fontWeight: 700, marginBottom: 24, color: brand.c.navy }}>
            Shipping Address
          </h2>
          
          {editing ? (
            <>
              <Input
                label="Street Address"
                value={formData.street}
                onChange={e => setFormData({ ...formData, street: e.target.value })}
              />
              <Input
                label="Apartment / Suite"
                value={formData.apt}
                onChange={e => setFormData({ ...formData, apt: e.target.value })}
              />
              <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: 16 }}>
                <Input
                  label="City"
                  value={formData.city}
                  onChange={e => setFormData({ ...formData, city: e.target.value })}
                />
                <Input
                  label="State"
                  type="select"
                  value={formData.state}
                  onChange={e => setFormData({ ...formData, state: e.target.value })}
                  options={US_STATES}
                />
                <Input
                  label="ZIP"
                  value={formData.zip}
                  onChange={e => setFormData({ ...formData, zip: e.target.value })}
                />
              </div>
            </>
          ) : (
            <div style={{ fontSize: 16, lineHeight: 1.6 }}>
              {user.address ? (
                <>
                  {user.address.street}
                  {user.address.apt && `, ${user.address.apt}`}
                  <br />
                  {user.address.city}, {user.address.state} {user.address.zip}
                </>
              ) : (
                <span style={{ color: '#6B7280' }}>No address on file</span>
              )}
            </div>
          )}
        </Card>
        
        {/* Insurance */}
        <Card>
          <h2 style={{ fontSize: 20, fontWeight: 700, marginBottom: 24, color: brand.c.navy }}>
            Insurance Information
          </h2>
          
          {editing ? (
            <>
              <Input
                label="Insurance Provider"
                value={formData.insuranceProvider}
                onChange={e => setFormData({ ...formData, insuranceProvider: e.target.value })}
                placeholder="e.g., Blue Cross Blue Shield"
              />
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 16 }}>
                <Input
                  label="Member ID"
                  value={formData.memberId}
                  onChange={e => setFormData({ ...formData, memberId: e.target.value })}
                />
                <Input
                  label="Group Number"
                  value={formData.groupNumber}
                  onChange={e => setFormData({ ...formData, groupNumber: e.target.value })}
                />
              </div>
            </>
          ) : (
            <>
              {user.insurance ? (
                <div style={{ display: 'grid', gap: 16 }}>
                  <div>
                    <div style={{ fontSize: 14, color: '#6B7280', marginBottom: 4 }}>Provider</div>
                    <div style={{ fontSize: 16, fontWeight: 600 }}>{user.insurance.provider}</div>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 16 }}>
                    <div>
                      <div style={{ fontSize: 14, color: '#6B7280', marginBottom: 4 }}>Member ID</div>
                      <div style={{ fontSize: 16, fontWeight: 600, fontFamily: 'monospace' }}>{user.insurance.memberId}</div>
                    </div>
                    <div>
                      <div style={{ fontSize: 14, color: '#6B7280', marginBottom: 4 }}>Group Number</div>
                      <div style={{ fontSize: 16, fontWeight: 600, fontFamily: 'monospace' }}>{user.insurance.groupNumber}</div>
                    </div>
                  </div>
                </div>
              ) : (
                <div style={{
                  padding: 16,
                  background: '#F9FAFB',
                  borderRadius: 8,
                  color: '#6B7280',
                  fontSize: 14
                }}>
                  No insurance information on file. {brand.name} pricing is often lower than insurance copays.
                </div>
              )}
            </>
          )}
        </Card>
      </div>
    </div>
  );
}
